import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Elements, ExpressCheckoutElement, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { useCart } from '@/hooks/useCart';
import { useAuth } from '@/context/AuthContext';
import { formatPrice } from '@/utils/formatters';
import { productService } from '@/services/product.service';
import { orderService } from '@/services/order.service';
import { settingsService } from '@/services/settings.service';
import { Product } from '@/types/product.types';
import { cartLineKey, getCartLineDisplayName, getCartLineUnitPrice } from '@/utils/cartLine';
import { CreateOrderDto, OrderItem } from '@/types/order.types';
import { CreateCheckoutSessionResponse } from '@/types/order.types';
import {
  CreditCardIcon,
  ShoppingCartIcon,
  TruckIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import '@/styles/checkout.css';
import '@/styles/stripe-checkout.css';

const envStripeKey = (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '').trim();
const isValidStripeKey = (k: string) =>
  (k.startsWith('pk_test_') || k.startsWith('pk_live_')) && k.length > 30 && !k.includes('xxxxxxxx');

interface WizardStripePaymentFormProps {
  session: CreateCheckoutSessionResponse;
  tt: (fr: string, en: string) => string;
}

const ApplePayMark = () => (
  <svg viewBox="0 0 24 24" className="stripe-method-card-icon" aria-hidden="true">
    <path
      fill="currentColor"
      d="M16.67 12.85c.02 2.08 1.82 2.78 1.84 2.79-.02.05-.29 1-.95 1.98-.58.84-1.17 1.68-2.12 1.7-.93.02-1.23-.55-2.3-.55-1.07 0-1.41.53-2.28.57-.92.03-1.62-.92-2.2-1.75-1.19-1.72-2.09-4.87-.87-7 .61-1.06 1.71-1.74 2.91-1.76.9-.02 1.76.61 2.3.61.53 0 1.54-.75 2.59-.64.44.02 1.68.18 2.47 1.34-.06.04-1.48.86-1.47 2.71ZM14.65 6.4c.48-.58.81-1.38.72-2.19-.69.03-1.53.46-2.03 1.04-.45.52-.84 1.34-.73 2.13.77.06 1.56-.39 2.04-.98Z"
    />
  </svg>
);

const GooglePayMark = () => (
  <svg viewBox="0 0 24 24" className="stripe-method-card-icon" aria-hidden="true">
    <path fill="#EA4335" d="M12.23 10.18v3.77h5.24c-.23 1.21-.92 2.23-1.97 2.92l3.18 2.47c1.85-1.71 2.92-4.22 2.92-7.19 0-.69-.06-1.35-.18-1.97h-9.19Z" />
    <path fill="#4285F4" d="M12 21.7c2.64 0 4.86-.87 6.48-2.36l-3.18-2.47c-.88.59-2 1-3.3 1-2.54 0-4.69-1.72-5.46-4.03H3.26v2.55A9.78 9.78 0 0 0 12 21.7Z" />
    <path fill="#FBBC05" d="M6.54 13.84a5.86 5.86 0 0 1 0-3.68V7.61H3.26a9.78 9.78 0 0 0 0 8.78l3.28-2.55Z" />
    <path fill="#34A853" d="M12 6.14c1.44 0 2.73.49 3.75 1.46l2.81-2.81C16.86 3.2 14.64 2.3 12 2.3a9.78 9.78 0 0 0-8.74 5.31l3.28 2.55C7.31 7.86 9.46 6.14 12 6.14Z" />
  </svg>
);

const WizardStripePaymentForm = ({ session, tt }: WizardStripePaymentFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedStripeMethod, setSelectedStripeMethod] = useState<'applePay' | 'googlePay' | 'card'>('card');
  const [availableWallets, setAvailableWallets] = useState<{ applePay: boolean; googlePay: boolean }>({
    applePay: false,
    googlePay: false,
  });
  const returnUrl = `${window.location.origin}/confirmation-commande?orderNumber=${encodeURIComponent(session.hemoOrderId)}`;

  const confirmPayment = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setSubmitting(false);
      setError(submitError.message || tt('Impossible de valider le moyen de paiement.', 'Unable to validate payment method.'));
      return;
    }
    const { error: confirmError } = await stripe.confirmPayment({
      elements,
      clientSecret: session.client_secret,
      confirmParams: { return_url: returnUrl },
    });
    if (confirmError) {
      setError(confirmError.message || tt('Le paiement a échoué.', 'Payment failed.'));
      setSubmitting(false);
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await confirmPayment();
  };

  return (
    <form className="stripe-payment-form wizard-stripe-form" onSubmit={onSubmit}>
      <div className="stripe-method-selector-cards">
        <button
          type="button"
          className={`stripe-method-card ${selectedStripeMethod === 'applePay' ? 'active' : ''} ${!availableWallets.applePay ? 'disabled' : ''}`}
          onClick={() => setSelectedStripeMethod('applePay')}
        >
          <span className="stripe-method-card-header">
            <ApplePayMark />
            <span className="stripe-method-card-title">Apple Pay</span>
          </span>
          <span className="stripe-method-card-desc">
            {availableWallets.applePay
              ? tt('Disponible sur cet appareil', 'Available on this device')
              : tt('Indisponible', 'Unavailable')}
          </span>
        </button>
        <button
          type="button"
          className={`stripe-method-card ${selectedStripeMethod === 'googlePay' ? 'active' : ''} ${!availableWallets.googlePay ? 'disabled' : ''}`}
          onClick={() => setSelectedStripeMethod('googlePay')}
        >
          <span className="stripe-method-card-header">
            <GooglePayMark />
            <span className="stripe-method-card-title">Google Pay</span>
          </span>
          <span className="stripe-method-card-desc">
            {availableWallets.googlePay
              ? tt('Disponible sur cet appareil', 'Available on this device')
              : tt('Indisponible', 'Unavailable')}
          </span>
        </button>
        <button
          type="button"
          className={`stripe-method-card ${selectedStripeMethod === 'card' ? 'active' : ''}`}
          onClick={() => setSelectedStripeMethod('card')}
        >
          <span className="stripe-method-card-header">
            <CreditCardIcon className="stripe-method-card-icon" />
            <span className="stripe-method-card-title">{tt('Carte bancaire', 'Bank card')}</span>
          </span>
          <span className="stripe-method-card-desc">{tt('Paiement Stripe sécurisé', 'Secure Stripe payment')}</span>
        </button>
      </div>

      <div className="stripe-wallets-probe" aria-hidden="true">
        <ExpressCheckoutElement
          onConfirm={confirmPayment}
          onReady={(event: any) => {
            const methods = event?.availablePaymentMethods || {};
            setAvailableWallets({
              applePay: !!methods.applePay,
              googlePay: !!methods.googlePay,
            });
          }}
          options={{
            paymentMethodOrder: ['apple_pay', 'google_pay', 'card'],
            buttonHeight: 55,
            buttonTheme: {
              applePay: 'white-outline',
            },
            layout: {
              maxColumns: 2,
              maxRows: 1,
              overflow: 'never',
            },
            paymentMethods: {
              applePay: 'always',
              googlePay: 'always',
            },
          }}
        />
      </div>

      {(selectedStripeMethod === 'applePay' || selectedStripeMethod === 'googlePay') && (
        <div className="stripe-wallets-block">
          <h3>{selectedStripeMethod === 'applePay' ? 'Apple Pay' : 'Google Pay'}</h3>
          {(selectedStripeMethod === 'applePay' && availableWallets.applePay) ||
          (selectedStripeMethod === 'googlePay' && availableWallets.googlePay) ? (
            <div className="stripe-wallets-native">
              <ExpressCheckoutElement
                onConfirm={confirmPayment}
                options={{
                  paymentMethodOrder:
                    selectedStripeMethod === 'applePay' ? ['apple_pay', 'google_pay', 'card'] : ['google_pay', 'apple_pay', 'card'],
                  buttonHeight: 55,
                  buttonTheme: {
                    applePay: 'white-outline',
                  },
                  layout: {
                    maxColumns: 1,
                    maxRows: 1,
                    overflow: 'never',
                  },
                  paymentMethods: {
                    applePay: 'always',
                    googlePay: 'always',
                  },
                }}
              />
            </div>
          ) : (
            <div className="stripe-wallet-unavailable">
              {tt(
                'Ce moyen de paiement n’est pas disponible sur cet appareil, ce navigateur ou ce domaine. Utilisez la carte bancaire.',
                'This payment method is not available on this device, browser, or domain. Use the card payment form.'
              )}
            </div>
          )}
        </div>
      )}

      {selectedStripeMethod === 'card' && (
        <div className="stripe-card-block">
          <h3>{tt('Carte bancaire', 'Bank card')}</h3>
          <div className="stripe-payment-element-wrapper">
            <PaymentElement />
          </div>
        </div>
      )}

      {error && <p className="stripe-error">{error}</p>}
      {selectedStripeMethod === 'card' && (
        <button className="stripe-pay-btn" type="submit" disabled={submitting || !stripe || !elements}>
          {submitting ? tt('Paiement en cours...', 'Processing...') : tt('Payer maintenant', 'Pay now')}
        </button>
      )}
    </form>
  );
};

interface CheckoutCartItem {
  id: number;
  quantity: number;
  price?: number;
  name?: string;
  selected_pack_id?: string;
  selected_pack_duration?: string;
}

interface GuestAuthForm {
  email: string;
  password: string;
}

interface CheckoutFormData {
  customer_title: string;
  customer_first_name: string;
  customer_last_name: string;
  customer_email: string;
  customer_phone: string;
  customer_address: string;
  customer_address_details: string;
  customer_address_extra: string;
  customer_city: string;
  customer_postal_code: string;
  customer_country: string;
  is_business: boolean;
  company_name: string;
  notes: string;
  card_number: string;
  card_expiry: string;
  card_cvv: string;
  cardholder_name: string;
}

const defaultFormData: CheckoutFormData = {
  customer_title: 'M.',
  customer_first_name: '',
  customer_last_name: '',
  customer_email: '',
  customer_phone: '',
  customer_address: '',
  customer_address_details: '',
  customer_address_extra: '',
  customer_city: '',
  customer_postal_code: '',
  customer_country: 'France',
  is_business: false,
  company_name: '',
  notes: '',
  card_number: '',
  card_expiry: '',
  card_cvv: '',
  cardholder_name: '',
};

export const CheckoutWizard = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { cart, clearCart, updateQuantity, removeFromCart } = useCart();
  const { user, login, loading: authLoading } = useAuth();
  const isEnglish = i18n.language.startsWith('en');
  const tt = (fr: string, en: string) => (isEnglish ? en : fr);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [products, setProducts] = useState<Map<number, Product>>(new Map());

  const [authForm, setAuthForm] = useState<GuestAuthForm>({ email: '', password: '' });
  const [authError, setAuthError] = useState('');
  const [authSubmitting, setAuthSubmitting] = useState(false);

  const [formData, setFormData] = useState<CheckoutFormData>(defaultFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const passwordResetSubject = encodeURIComponent(tt('Mot de passe oublié', 'Forgot password'));

  const [citySuggestions, setCitySuggestions] = useState<string[]>([]);
  const [cityNameSuggestions, setCityNameSuggestions] = useState<string[]>([]);
  const [postalSuggestions, setPostalSuggestions] = useState<string[]>([]);
  const [cityLookupStatus, setCityLookupStatus] = useState<'idle' | 'loading' | 'not_found'>('idle');
  const [cityNameLookupStatus, setCityNameLookupStatus] = useState<'idle' | 'loading' | 'not_found'>('idle');
  const [postalLookupStatus, setPostalLookupStatus] = useState<'idle' | 'loading' | 'not_found'>('idle');
  const autoFillGuardRef = useRef<{ field: 'customer_city' | 'customer_postal_code'; value: string } | null>(null);

  const [stripeSession, setStripeSession] = useState<CreateCheckoutSessionResponse | null>(null);
  const [stripeSessionLoading, setStripeSessionLoading] = useState(false);
  const [stripeSessionError, setStripeSessionError] = useState<string | null>(null);
  const [stripePublishableKey, setStripePublishableKey] = useState<string | null>(null);

  const effectiveStripeKey =
    stripePublishableKey && stripePublishableKey.trim() ? stripePublishableKey : envStripeKey;
  const hasValidStripeKey = isValidStripeKey(effectiveStripeKey);
  const stripePromise = useMemo(
    () => (hasValidStripeKey ? loadStripe(effectiveStripeKey) : null),
    [hasValidStripeKey, effectiveStripeKey]
  );

  const getCartItems = (): CheckoutCartItem[] => {
    try {
      const stored = localStorage.getItem('cart');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return parsed
            .filter(
              (item: CheckoutCartItem) =>
                item && typeof item.id === 'number' && typeof item.quantity === 'number'
            )
            .map((item: CheckoutCartItem) => ({
              id: item.id,
              quantity: item.quantity,
              price: item.price,
              name: item.name,
              selected_pack_id:
                item.selected_pack_id != null ? String(item.selected_pack_id) : undefined,
              selected_pack_duration: item.selected_pack_duration,
            }));
        }
      }
    } catch (error) {
      console.error('Erreur lecture panier:', error);
    }
    return cart
      .filter((item) => typeof item.id === 'number' && typeof item.quantity === 'number')
      .map((item) => ({
        id: item.id,
        quantity: item.quantity,
        price: item.price,
        name: item.name,
        selected_pack_id:
          item.selected_pack_id != null ? String(item.selected_pack_id) : undefined,
        selected_pack_duration: item.selected_pack_duration,
      }));
  };

  const cartItems = getCartItems();

  useEffect(() => {
    if (user) {
      const parts = (user.fullName || '').trim().split(/\s+/).filter(Boolean);
      const firstName = parts[0] || '';
      const lastName = parts.slice(1).join(' ');
      setFormData((prev) => ({
        ...prev,
        customer_first_name: firstName || prev.customer_first_name,
        customer_last_name: lastName || prev.customer_last_name,
        customer_email: user.email || prev.customer_email,
      }));
      setAuthForm((prev) => ({ ...prev, email: user.email || prev.email }));
    }
  }, [user]);

  useEffect(() => {
    if (cartItems.length === 0) {
      navigate('/');
      return;
    }
    void loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchStripeKey = async () => {
      try {
        const key = await settingsService.getStripePublishableKey();
        setStripePublishableKey(key || '');
      } catch {
        setStripePublishableKey('');
      }
    };
    void fetchStripeKey();
  }, []);

  const createStripeSession = async () => {
    if (!stripePromise || !formData.customer_email || !formData.customer_first_name || !formData.customer_last_name) return;
    setStripeSessionLoading(true);
    setStripeSessionError(null);
    setStripeSession(null);
    try {
      const customerName = [formData.customer_title, formData.customer_first_name, formData.customer_last_name]
        .filter(Boolean)
        .join(' ')
        .trim();
      const customerAddress = [
        formData.customer_address,
        formData.customer_address_details,
        formData.customer_address_extra,
      ]
        .map((v) => String(v || '').trim())
        .filter(Boolean)
        .join(', ');
      const items = cartItems
        .map((item) => {
          const p = products.get(item.id);
          if (!p) return null;
          const price = getCartLineUnitPrice(item, p);
          const name = getCartLineDisplayName(item, p) || p.name || `Produit ${p.id}`;
          return { id: p.id, name, price, quantity: item.quantity };
        })
        .filter(Boolean) as { id: number; name: string; price: number; quantity: number }[];
      const sub = items.reduce((s, i) => s + i.price * i.quantity, 0);
      const response = await orderService.createCheckoutSession({
        customer_name: customerName,
        customer_email: formData.customer_email.trim(),
        customer_phone: formData.customer_phone.trim(),
        customer_address: customerAddress || tt('Adresse non précisée', 'Address not specified'),
        customer_city: formData.customer_city.trim(),
        customer_postal_code: formData.customer_postal_code.trim(),
        items,
        total: sub,
        currency: 'eur',
      });
      setStripeSession(response);
    } catch (err: unknown) {
      setStripeSessionError(
        (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error ||
          (err as Error)?.message ||
          tt('Impossible de préparer le paiement.', 'Unable to prepare payment.')
      );
    } finally {
      setStripeSessionLoading(false);
    }
  };

  const hasRequiredCustomerData = useMemo(
    () =>
      !!(
        formData.customer_first_name?.trim() &&
        formData.customer_last_name?.trim() &&
        formData.customer_email?.trim() &&
        formData.customer_phone?.trim() &&
        formData.customer_postal_code?.trim() &&
        formData.customer_city?.trim()
      ),
    [formData]
  );

  useEffect(() => {
    const canCreateSession =
      step === 6 &&
      stripePromise &&
      hasRequiredCustomerData &&
      !stripeSession &&
      !stripeSessionLoading &&
      products.size > 0;
    if (canCreateSession) {
      void createStripeSession();
    }
    if (step !== 6) {
      setStripeSession(null);
      setStripeSessionError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- createStripeSession uses formData from closure
  }, [step, hasRequiredCustomerData, products.size]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const productMap = new Map<number, Product>();
      for (const item of cartItems) {
        const product = await productService.getById(item.id);
        productMap.set(item.id, product);
      }
      setProducts(productMap);
    } catch (error) {
      console.error('Erreur chargement produits checkout:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (formData.customer_country !== 'France') {
      setCitySuggestions([]);
      setCityNameSuggestions([]);
      setPostalSuggestions([]);
      setCityLookupStatus('idle');
      setCityNameLookupStatus('idle');
      setPostalLookupStatus('idle');
    }
  }, [formData.customer_country]);

  useEffect(() => {
    if (formData.customer_country !== 'France') return;

    const postalCode = formData.customer_postal_code.trim();
    const isPostalAutoFill =
      autoFillGuardRef.current?.field === 'customer_postal_code' &&
      autoFillGuardRef.current?.value === postalCode;

    if (isPostalAutoFill) {
      window.setTimeout(() => {
        autoFillGuardRef.current = null;
      }, 0);
      return;
    }

    if (!/^\d{5}$/.test(postalCode)) {
      setCitySuggestions([]);
      setCityLookupStatus('idle');
      setCityNameSuggestions([]);
      setCityNameLookupStatus('idle');
      return;
    }

    setCityLookupStatus('loading');
    setCitySuggestions([]);
    setCityNameSuggestions([]);
    setCityNameLookupStatus('idle');

    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await fetch(
          `https://geo.api.gouv.fr/communes?codePostal=${postalCode}&fields=nom,codesPostaux&format=json`
        );
        const data = await response.json();
        const cities = Array.from(new Set((data || []).map((item: { nom?: string }) => item.nom))).filter(Boolean) as string[];

        if (cities.length === 0) {
          setCityLookupStatus('not_found');
          setCitySuggestions([]);
          return;
        }

        setCityLookupStatus('idle');
        if (cities.length === 1) {
          autoFillGuardRef.current = { field: 'customer_city', value: cities[0] };
          setFormData((prev) => ({ ...prev, customer_city: cities[0] }));
          setCitySuggestions([]);
        } else {
          setCitySuggestions(cities);
        }
      } catch (error) {
        console.error('Erreur lookup ville:', error);
        setCityLookupStatus('not_found');
        setCitySuggestions([]);
      }
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [formData.customer_postal_code, formData.customer_country]);

  useEffect(() => {
    if (formData.customer_country !== 'France') return;

    const city = formData.customer_city.trim();
    const isCityAutoFill =
      autoFillGuardRef.current?.field === 'customer_city' &&
      autoFillGuardRef.current?.value === city;

    if (isCityAutoFill) {
      window.setTimeout(() => {
        autoFillGuardRef.current = null;
      }, 0);
      return;
    }

    if (city.length < 2) {
      setPostalSuggestions([]);
      setPostalLookupStatus('idle');
      setCityNameSuggestions([]);
      setCityNameLookupStatus('idle');
      return;
    }

    setPostalLookupStatus('loading');
    setPostalSuggestions([]);
    setCityNameLookupStatus('loading');
    setCityNameSuggestions([]);

    const timeoutId = window.setTimeout(async () => {
      try {
        const [postalResponse, cityResponse] = await Promise.all([
          fetch(
            `https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(city)}&fields=nom,codesPostaux&boost=population&limit=8`
          ),
          fetch(
            `https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(city)}&fields=nom&boost=population&limit=8`
          ),
        ]);

        const postalData = await postalResponse.json();
        const cityData = await cityResponse.json();

        const matches = (postalData || []).filter((item: { nom?: string }) => item?.nom);
        const exactMatches = matches.filter(
          (item: { nom: string }) => item.nom.toLowerCase() === city.toLowerCase()
        );
        const codes = Array.from(
          new Set((exactMatches.length > 0 ? exactMatches : matches).flatMap((item: { codesPostaux?: string[] }) => item.codesPostaux || []))
        ).filter(Boolean) as string[];

        const cities = Array.from(new Set((cityData || []).map((item: { nom?: string }) => item.nom))).filter(Boolean) as string[];

        if (codes.length === 0) {
          setPostalLookupStatus('not_found');
          setPostalSuggestions([]);
        } else {
          setPostalLookupStatus('idle');
          if (codes.length === 1) {
            autoFillGuardRef.current = { field: 'customer_postal_code', value: codes[0] };
            setFormData((prev) => ({ ...prev, customer_postal_code: codes[0] }));
            setPostalSuggestions([]);
          } else {
            setPostalSuggestions(codes);
          }
        }

        if (cities.length === 0) {
          setCityNameLookupStatus('not_found');
          setCityNameSuggestions([]);
        } else {
          setCityNameLookupStatus('idle');
          setCityNameSuggestions(cities);
        }
      } catch (error) {
        console.error('Erreur lookup code postal/ville:', error);
        setPostalLookupStatus('not_found');
        setPostalSuggestions([]);
        setCityNameLookupStatus('not_found');
        setCityNameSuggestions([]);
      }
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [formData.customer_city, formData.customer_country]);

  const mergedCitySuggestions = useMemo(
    () => Array.from(new Set([...citySuggestions, ...cityNameSuggestions])),
    [citySuggestions, cityNameSuggestions]
  );

  const subtotal = cartItems.reduce((sum, item) => {
    const product = products.get(item.id);
    if (!product) return sum;
    const unit = getCartLineUnitPrice(item, product);
    return sum + unit * item.quantity;
  }, 0);

  const shippingCost = 0;
  const total = subtotal + shippingCost;
  const stepDefinitions = [
    { id: 1, label: tt('Panier', 'Cart') },
    { id: 2, label: tt('Résumé', 'Summary') },
    { id: 3, label: tt('Compte', 'Account') },
    { id: 4, label: tt('Livraison', 'Shipping') },
    { id: 5, label: tt('Adresse', 'Address') },
    { id: 6, label: tt('Paiement', 'Payment') },
    { id: 7, label: tt('Confirmation', 'Confirmation') },
  ] as const;

  const setField = (name: keyof CheckoutFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleCardFieldChange = (name: 'card_number' | 'card_expiry' | 'card_cvv' | 'cardholder_name', value: string) => {
    let formattedValue = value;
    if (name === 'card_number') {
      const numbers = value.replace(/\s/g, '').replace(/\D/g, '').slice(0, 16);
      formattedValue = numbers.match(/.{1,4}/g)?.join(' ') || numbers;
    }
    if (name === 'card_expiry') {
      const numbers = value.replace(/\D/g, '').slice(0, 4);
      formattedValue = numbers.length >= 2 ? `${numbers.slice(0, 2)}${numbers.length > 2 ? `/${numbers.slice(2, 4)}` : ''}` : numbers;
    }
    if (name === 'card_cvv') {
      formattedValue = value.replace(/\D/g, '').slice(0, 4);
    }
    setField(name, formattedValue);
  };

  const handleSelectCity = (city: string) => {
    setField('customer_city', city);
    setCitySuggestions([]);
    setCityNameSuggestions([]);
    setCityLookupStatus('idle');
    setCityNameLookupStatus('idle');
  };

  const handleSelectPostalCode = (postalCode: string) => {
    autoFillGuardRef.current = { field: 'customer_postal_code', value: postalCode };
    setField('customer_postal_code', postalCode);
    setPostalSuggestions([]);
    setPostalLookupStatus('idle');
  };

  const goToStep = (nextStep: number) => {
    setStep(nextStep);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToPreviousStep = () => {
    if (step > 1) {
      goToStep(step - 1);
    }
  };

  const validateStep4 = () => {
    const nextErrors: Record<string, string> = {};
    if (!formData.customer_postal_code.trim() || !/^\d{5}$/.test(formData.customer_postal_code.trim())) {
      nextErrors.customer_postal_code = tt('Veuillez saisir un code postal valide (5 chiffres).', 'Please enter a valid postal code (5 digits).');
    }
    if (!formData.customer_city.trim()) {
      nextErrors.customer_city = tt('Veuillez saisir la ville.', 'Please enter your city.');
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return false;
    return true;
  };

  const validateStep5 = () => {
    const nextErrors: Record<string, string> = {};
    if (!formData.customer_first_name.trim()) nextErrors.customer_first_name = tt('Le prénom est requis.', 'First name is required.');
    if (!formData.customer_last_name.trim()) nextErrors.customer_last_name = tt('Le nom de famille est requis.', 'Last name is required.');
    if (!formData.customer_email.trim()) {
      nextErrors.customer_email = tt("L'adresse email est requise.", 'Email is required.');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customer_email)) {
      nextErrors.customer_email = tt('Email invalide.', 'Invalid email.');
    }
    if (!formData.customer_phone.trim()) {
      nextErrors.customer_phone = tt('Le numéro de téléphone est requis.', 'Phone number is required.');
    }
    if (!formData.customer_postal_code.trim()) {
      nextErrors.customer_postal_code = tt('Le code postal est requis.', 'Postal code is required.');
    }
    if (!formData.customer_city.trim()) {
      nextErrors.customer_city = tt('La ville est requise.', 'City is required.');
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateStep6 = () => {
    const nextErrors: Record<string, string> = {};
    if (!stripePromise) {
      const cardNumberDigits = formData.card_number.replace(/\s/g, '');
      if (!cardNumberDigits || cardNumberDigits.length !== 16) {
        nextErrors.card_number = tt('Le numéro de carte doit contenir 16 chiffres.', 'Card number must contain 16 digits.');
      }
      if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(formData.card_expiry)) {
        nextErrors.card_expiry = tt('Format invalide (MM/YY).', 'Invalid format (MM/YY).');
      }
      if (!/^\d{3,4}$/.test(formData.card_cvv)) {
        nextErrors.card_cvv = tt('Le CVV doit contenir 3 ou 4 chiffres.', 'CVV must contain 3 or 4 digits.');
      }
      if (!formData.cardholder_name.trim()) {
        nextErrors.cardholder_name = tt('Le nom du titulaire est requis.', 'Cardholder name is required.');
      }
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleLoginAndContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    if (!authForm.email.trim() || !authForm.password.trim()) {
      setAuthError(tt('Veuillez renseigner votre email et votre mot de passe.', 'Please enter your email and password.'));
      return;
    }
    setAuthSubmitting(true);
    try {
      await login({ email: authForm.email.trim(), password: authForm.password });
      goToStep(4);
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : tt('Connexion impossible.', 'Login failed.'));
    } finally {
      setAuthSubmitting(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (!validateStep6()) return;
    if (cartItems.length === 0) {
      navigate('/');
      return;
    }

    setSubmitting(true);
    try {
      const orderItems: OrderItem[] = cartItems.map((item) => {
        const product = products.get(item.id);
        if (!product) throw new Error(`Produit ${item.id} introuvable`);
        const unit = getCartLineUnitPrice(item, product);
        const label = getCartLineDisplayName(item, product) || product.name;
        return {
          product_id: product.id,
          product_name: label,
          quantity: item.quantity,
          price: unit,
          total: unit * item.quantity,
        };
      });

      const customerName = [formData.customer_title, formData.customer_first_name, formData.customer_last_name]
        .filter(Boolean)
        .join(' ')
        .trim();
      const customerAddress = [
        formData.customer_address,
        formData.customer_address_details,
        formData.customer_address_extra,
      ]
        .map((value) => value.trim())
        .filter(Boolean)
        .join(', ');

      const orderData: CreateOrderDto = {
        customer_name: customerName,
        customer_email: formData.customer_email.trim(),
        customer_phone: formData.customer_phone.trim(),
        customer_address: customerAddress || tt('Adresse non précisée', 'Address not specified'),
        customer_city: formData.customer_city.trim(),
        customer_postal_code: formData.customer_postal_code.trim(),
        customer_type: formData.is_business ? 'professionnel' : 'particulier',
        company_name: formData.is_business ? (formData.company_name.trim() || null) : null,
        items: orderItems,
        subtotal,
        shipping_cost: shippingCost,
        total,
        payment_status: 'pending',
        order_status: 'pending',
      };

      const order = await orderService.create(orderData);
      sessionStorage.setItem('lastOrder', JSON.stringify(order));
      clearCart();
      goToStep(7);
      navigate(`/confirmation-commande?orderNumber=${order.order_number}`);
    } catch (error: unknown) {
      console.error('Erreur création commande:', error);
      alert(tt('Une erreur est survenue lors de la création de la commande.', 'An error occurred while creating the order.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div style={{ padding: '100px 20px', textAlign: 'center' }}>
          <div style={{ color: '#999' }}>{tt('Chargement du checkout...', 'Loading checkout...')}</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen checkout-page-root">
      <Navbar />
      <div className="checkout-container checkout-wizard-container">
        <div className="checkout-breadcrumb">
          <Link to="/" className="breadcrumb-link">{tt('Accueil', 'Home')}</Link>
          <span className="breadcrumb-separator"> &gt; </span>
          <span className="breadcrumb-current">{tt('Checkout', 'Checkout')}</span>
        </div>

        <div className="checkout-content checkout-wizard-content">
          <h1 className="checkout-title">{tt('Checkout', 'Checkout')}</h1>

          <div className="checkout-steps-indicator">
            {stepDefinitions.map((stepItem) => (
              <div
                key={stepItem.id}
                className={`step-pill ${stepItem.id === step ? 'active' : ''} ${stepItem.id < step ? 'done' : ''}`}
                onClick={() => {
                  if (stepItem.id <= step) {
                    goToStep(stepItem.id);
                  }
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && stepItem.id <= step) {
                    e.preventDefault();
                    goToStep(stepItem.id);
                  }
                }}
              >
                {stepItem.label}
              </div>
            ))}
          </div>

          {step === 1 && (
            <section className="wizard-block">
              <h2 className="form-section-title">
                <ShoppingCartIcon className="section-icon" />
                {tt('Vérifiez votre produit', 'Review your product')}
              </h2>
              <div className="wizard-products-grid">
                {cartItems.map((item) => {
                  const product = products.get(item.id);
                  if (!product) return null;
                  const price = getCartLineUnitPrice(item, product);
                  const lineLabel = getCartLineDisplayName(item, product) || product.name;
                  return (
                    <article key={cartLineKey(item)} className="wizard-product-card">
                      <img
                        src={product.image_url || '/images/bottle.png'}
                        alt={lineLabel}
                        className="order-item-image"
                      />
                      <div className="wizard-product-info">
                        <h3>{lineLabel}</h3>
                        <p>
                          {product.description?.trim()
                            ? `${product.description.slice(0, 140)}${product.description.length > 140 ? '...' : ''}`
                            : tt('Produit naturel Hémo-J formulé pour votre bien-être quotidien.', 'Natural Hémo-J product designed for your daily well-being.')}
                        </p>
                        <p className="wizard-price">{formatPrice(price)} x {item.quantity}</p>
                        <div className="wizard-qty-controls">
                          <button
                            type="button"
                            className="btn-secondary-checkout qty-btn"
                            onClick={() =>
                              updateQuantity(item.id, Math.max(1, item.quantity - 1), item.selected_pack_id)
                            }
                            disabled={item.quantity <= 1}
                          >
                            -
                          </button>
                          <span className="qty-value">{item.quantity}</span>
                          <button
                            type="button"
                            className="btn-secondary-checkout qty-btn"
                            onClick={() => {
                              const maxQty = product.stock_unlimited ? 999 : (product.stock_quantity || 1);
                              updateQuantity(
                                item.id,
                                Math.min(maxQty, item.quantity + 1),
                                item.selected_pack_id
                              );
                            }}
                          >
                            +
                          </button>
                          <button
                            type="button"
                            className="btn-secondary-checkout"
                            onClick={() => {
                              removeFromCart(item.id, item.selected_pack_id);
                              const nextCartRaw = localStorage.getItem('cart');
                              const nextCart = nextCartRaw ? JSON.parse(nextCartRaw) : [];
                              if (!Array.isArray(nextCart) || nextCart.length === 0) {
                                navigate('/');
                              }
                            }}
                          >
                            {tt('Annuler', 'Remove')}
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
              <div className="wizard-actions">
                <button type="button" className="btn-place-order" onClick={() => goToStep(2)}>
                  {tt('Continuer', 'Continue')}
                </button>
                <button type="button" className="btn-secondary-checkout" onClick={() => goToStep(2)}>
                  {tt('Consulter votre Panier', 'Review your cart')}
                </button>
                <button type="button" className="btn-secondary-checkout" onClick={() => navigate('/')}>
                  {tt('Continuer dans le site', 'Continue on the site')}
                </button>
                <button type="button" className="btn-secondary-checkout" onClick={goToPreviousStep} disabled={step <= 1}>
                  {tt("Retour à l'étape précédente", 'Back to previous step')}
                </button>
              </div>
            </section>
          )}

          {step === 2 && (
            <section className="wizard-block">
              <h2 className="form-section-title">
                <ShoppingCartIcon className="section-icon" />
                {tt('Détail de votre panier', 'Cart details')}
              </h2>
              <div className="checkout-summary">
                <h3 className="summary-title">{tt('Le montant total de votre panier', 'Your cart total')}</h3>
                <div className="order-items">
                  {cartItems.map((item) => {
                    const product = products.get(item.id);
                    if (!product) return null;
                    const unit = getCartLineUnitPrice(item, product);
                    const lineLabel = getCartLineDisplayName(item, product) || product.name;
                    return (
                      <div key={cartLineKey(item)} className="order-item">
                        <img src={product.image_url || '/images/bottle.png'} alt={lineLabel} className="order-item-image" />
                        <div className="order-item-details">
                          <h3>{lineLabel}</h3>
                          <p className="order-item-quantity">{tt('Quantité', 'Quantity')} {item.quantity}</p>
                          <p className="order-item-price">{formatPrice(unit * item.quantity)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="order-totals">
                  <div className="total-row">
                    <span>{tt('Sous-total', 'Subtotal')}</span>
                    <span>{formatPrice(subtotal)}</span>
                  </div>
                  <div className="total-row total-final">
                    <span>{tt('Total', 'Total')}</span>
                    <span>{formatPrice(subtotal)}</span>
                  </div>
                </div>
              </div>
              <div className="wizard-actions">
                <button type="button" className="btn-place-order" onClick={() => goToStep(3)}>
                  {tt('Valider la commande', 'Validate order')}
                </button>
                <button type="button" className="btn-secondary-checkout" onClick={() => navigate('/')}>
                  {tt('Continuer dans le site', 'Continue on the site')}
                </button>
                <button type="button" className="btn-secondary-checkout" onClick={goToPreviousStep}>
                  {tt("Retour à l'étape précédente", 'Back to previous step')}
                </button>
              </div>
            </section>
          )}

          {step === 3 && (
            <section className="wizard-block">
              <h2 className="form-section-title">
                <UserPlusIcon className="section-icon" />
                {tt('Connectez-vous pour régler vos achats plus rapidement.', 'Sign in to pay faster.')}
              </h2>

              <div className="wizard-choice-grid">
                <div className="wizard-choice-card">
                  <h3>{tt('1 - Passez commande avec votre Compte Hémo-J', '1 - Place your order with your Hémo-J account')}</h3>
                  {user && !authLoading ? (
                    <>
                      <p>{tt('Vous êtes déjà connecté avec', 'You are already signed in with')} {user.email}.</p>
                      <button type="button" className="btn-place-order" onClick={() => goToStep(4)}>
                        {tt('Continuer', 'Continue')}
                      </button>
                    </>
                  ) : (
                    <form onSubmit={handleLoginAndContinue} className="wizard-inline-form">
                      <div className="form-group">
                        <label htmlFor="auth_email">{tt('Email', 'Email')}</label>
                        <input
                          id="auth_email"
                          type="email"
                          value={authForm.email}
                          onChange={(e) => setAuthForm((prev) => ({ ...prev, email: e.target.value }))}
                          placeholder={tt('Votre email', 'Your email')}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="auth_password">{tt('Mot de passe', 'Password')}</label>
                        <input
                          id="auth_password"
                          type="password"
                          value={authForm.password}
                          onChange={(e) => setAuthForm((prev) => ({ ...prev, password: e.target.value }))}
                          placeholder={tt('Votre mot de passe', 'Your password')}
                        />
                      </div>
                      {authError && <p className="error-message">{authError}</p>}
                      <div className="wizard-inline-links">
                        <a href={`mailto:support@hemo-j.com?subject=${passwordResetSubject}`}>{tt('Mot de passe oublié ?', 'Forgot password?')}</a>
                        <Link to="/register">{tt('Créer un compte', 'Create account')}</Link>
                      </div>
                      <button type="submit" className="btn-place-order" disabled={authSubmitting}>
                        {authSubmitting ? tt('Connexion...', 'Signing in...') : tt('Se connecter et continuer', 'Sign in and continue')}
                      </button>
                    </form>
                  )}
                </div>

                <div className="wizard-choice-card">
                  <h3>{tt("2 - Vous n'avez pas de compte Hémo-J", "2 - You don't have a Hémo-J account")}</h3>
                  <p>{tt('Poursuivez ainsi. Vous créerez un compte Hémo-J ultérieurement.', 'Continue as guest. You can create a Hémo-J account later.')}</p>
                  <button type="button" className="btn-place-order" onClick={() => goToStep(4)}>
                    {tt('Continuer comme invité', 'Continue as guest')}
                  </button>
                </div>
              </div>
              <div className="wizard-actions">
                <button type="button" className="btn-secondary-checkout" onClick={goToPreviousStep}>
                  {tt("Retour à l'étape précédente", 'Back to previous step')}
                </button>
              </div>
            </section>
          )}

          {step === 4 && (
            <section className="wizard-block">
              <h2 className="form-section-title">
                <TruckIcon className="section-icon" />
                {tt('Livraison', 'Shipping')}
              </h2>
              <p className="wizard-subtitle">{tt('Comment souhaitez-vous recevoir votre commande ?', 'How would you like to receive your order?')}</p>
              <p className="wizard-subtitle">{tt('Saisissez un code postal pour connaître les options de livraison.', 'Enter a postal code to see shipping options.')}</p>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="customer_postal_code">{tt('Code postal', 'Postal code')}</label>
                  <input
                    id="customer_postal_code"
                    type="text"
                    value={formData.customer_postal_code}
                    onChange={(e) => setField('customer_postal_code', e.target.value)}
                    placeholder="00000"
                  />
                  {formData.customer_country === 'France' &&
                    (postalLookupStatus !== 'idle' || postalSuggestions.length > 0) && (
                      <div className="suggestions-dropdown">
                        {postalLookupStatus === 'loading' && (
                          <div className="suggestion-message">{tt('Recherche des codes postaux...', 'Searching postal codes...')}</div>
                        )}
                        {postalLookupStatus === 'not_found' && postalSuggestions.length === 0 && (
                          <div className="suggestion-message">{tt('Aucun code postal trouvé.', 'No postal code found.')}</div>
                        )}
                        {postalSuggestions.map((postalCode) => (
                          <button
                            key={postalCode}
                            type="button"
                            className="suggestion-item"
                            onClick={() => handleSelectPostalCode(postalCode)}
                          >
                            {postalCode}
                          </button>
                        ))}
                      </div>
                    )}
                  {errors.customer_postal_code && <span className="error-message">{errors.customer_postal_code}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="customer_city">{tt('Ville', 'City')}</label>
                  <input
                    id="customer_city"
                    type="text"
                    value={formData.customer_city}
                    onChange={(e) => setField('customer_city', e.target.value)}
                    placeholder={tt('Ville', 'City')}
                  />
                  {formData.customer_country === 'France' &&
                    (cityLookupStatus !== 'idle' ||
                      cityNameLookupStatus !== 'idle' ||
                      mergedCitySuggestions.length > 0) && (
                      <div className="suggestions-dropdown">
                        {(cityLookupStatus === 'loading' || cityNameLookupStatus === 'loading') && (
                          <div className="suggestion-message">{tt('Recherche des villes...', 'Searching cities...')}</div>
                        )}
                        {(cityLookupStatus === 'not_found' || cityNameLookupStatus === 'not_found') &&
                          mergedCitySuggestions.length === 0 && (
                            <div className="suggestion-message">{tt('Aucune ville trouvée.', 'No city found.')}</div>
                          )}
                        {mergedCitySuggestions.map((city) => (
                          <button
                            key={city}
                            type="button"
                            className="suggestion-item"
                            onClick={() => handleSelectCity(city)}
                          >
                            {city}
                          </button>
                        ))}
                      </div>
                    )}
                  {errors.customer_city && <span className="error-message">{errors.customer_city}</span>}
                </div>
              </div>

              <div className="wizard-delivery-modes">
                <h3>{tt('Sélectionnez votre mode de livraison :', 'Select your shipping method:')}</h3>
                <label className="radio-option">
                  <input type="radio" checked readOnly />
                  <span>{tt('Livraison standard (Gratuite)', 'Standard shipping (Free)')}</span>
                </label>
                <button
                  type="button"
                  className="btn-place-order"
                  onClick={() => {
                    if (validateStep4()) {
                      goToStep(5);
                    }
                  }}
                >
                  {tt("Passer à l'adresse d'expédition", 'Proceed to shipping address')}
                </button>
              </div>
              <div className="wizard-actions">
                <button type="button" className="btn-secondary-checkout" onClick={goToPreviousStep}>
                  {tt("Retour à l'étape précédente", 'Back to previous step')}
                </button>
              </div>
            </section>
          )}

          {step === 5 && (
            <section className="wizard-block step-five-block">
              <h2 className="form-section-title">{tt('Où devons-nous envoyer votre commande ?', 'Where should we send your order?')}</h2>
              <p className="wizard-subtitle">{tt('Saisissez vos nom et adresse :', 'Enter your name and address:')}</p>

              <div className="form-group">
                <label htmlFor="customer_title">{tt('Titre', 'Title')}</label>
                <select
                  id="customer_title"
                  value={formData.customer_title}
                  onChange={(e) => setField('customer_title', e.target.value)}
                >
                  <option>{tt('M.', 'Mr.')}</option>
                  <option>{tt('Mme', 'Mrs.')}</option>
                  <option>{tt('Mlle', 'Ms.')}</option>
                  <option>Dr</option>
                  <option>{tt('Maître', 'Master')}</option>
                  <option>{tt('Non précisé', 'Not specified')}</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="customer_first_name">{tt('Prénom', 'First name')}</label>
                  <input
                    id="customer_first_name"
                    value={formData.customer_first_name}
                    onChange={(e) => setField('customer_first_name', e.target.value)}
                  />
                  {errors.customer_first_name && <span className="error-message">{errors.customer_first_name}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="customer_last_name">{tt('Nom de famille', 'Last name')}</label>
                  <input
                    id="customer_last_name"
                    value={formData.customer_last_name}
                    onChange={(e) => setField('customer_last_name', e.target.value)}
                  />
                  {errors.customer_last_name && <span className="error-message">{errors.customer_last_name}</span>}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="customer_address">{tt('Numero et nom de la rue (facultatif)', 'Street number and name (optional)')}</label>
                <input
                  id="customer_address"
                  value={formData.customer_address}
                  onChange={(e) => setField('customer_address', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="customer_address_details">{tt("N° d'apt, étage, code", 'Apartment, floor, code')}</label>
                <input
                  id="customer_address_details"
                  value={formData.customer_address_details}
                  onChange={(e) => setField('customer_address_details', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="customer_address_extra">{tt("Information d'adresse supplémentaire (facultatif)", 'Additional address information (optional)')}</label>
                <input
                  id="customer_address_extra"
                  value={formData.customer_address_extra}
                  onChange={(e) => setField('customer_address_extra', e.target.value)}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="saved_postal">{tt('Code postal (déjà enregistré)', 'Postal code (already set)')}</label>
                  <input id="saved_postal" value={formData.customer_postal_code} readOnly />
                  {errors.customer_postal_code && <span className="error-message">{errors.customer_postal_code}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="saved_city">{tt('Ville (déjà enregistrée)', 'City (already set)')}</label>
                  <input id="saved_city" value={formData.customer_city} readOnly />
                  {errors.customer_city && <span className="error-message">{errors.customer_city}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="saved_country">{tt('Pays-Région', 'Country/Region')}</label>
                  <select
                    id="saved_country"
                    value={formData.customer_country}
                    onChange={(e) => setField('customer_country', e.target.value)}
                  >
                    <option value="France">France</option>
                  </select>
                </div>
              </div>

              <label className="form-checkbox">
                <input
                  type="checkbox"
                  checked={formData.is_business}
                  onChange={(e) => setField('is_business', e.target.checked)}
                />
                <span>{tt("Il s'agit d'une adresse professionnelle", 'This is a business address')}</span>
              </label>

              {formData.is_business && (
                <div className="form-group">
                  <label htmlFor="company_name">{tt('Nom de la société (facultatif)', 'Company name (optional)')}</label>
                  <input
                    id="company_name"
                    value={formData.company_name}
                    onChange={(e) => setField('company_name', e.target.value)}
                  />
                </div>
              )}

              <h3 className="form-section-subtitle">{tt('Quelles sont vos coordonnées ?', 'What are your contact details?')}</h3>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="customer_email">{tt('Adresse email', 'Email address')}</label>
                  <input
                    id="customer_email"
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => setField('customer_email', e.target.value)}
                  />
                  {errors.customer_email && <span className="error-message">{errors.customer_email}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="customer_phone">{tt('Numéro de téléphone', 'Phone number')}</label>
                  <input
                    id="customer_phone"
                    value={formData.customer_phone}
                    onChange={(e) => setField('customer_phone', e.target.value)}
                  />
                  {errors.customer_phone && <span className="error-message">{errors.customer_phone}</span>}
                </div>
              </div>

              <div className="wizard-actions">
                <button
                  type="button"
                  className="btn-place-order"
                  onClick={() => {
                    if (validateStep5()) {
                      goToStep(6);
                    }
                  }}
                >
                  {tt('Passer au paiement', 'Proceed to payment')}
                </button>
                <button type="button" className="btn-secondary-checkout" onClick={goToPreviousStep}>
                  {tt("Retour à l'étape précédente", 'Back to previous step')}
                </button>
              </div>
            </section>
          )}

          {step === 6 && (
            <section className="wizard-block">
              <h2 className="form-section-title">
                <CreditCardIcon className="section-icon" />
                {tt('Paiement', 'Payment')}
              </h2>
              <div className="payment-method-section">
                <h3 className="form-section-subtitle">{tt('Mode de paiement disponible', 'Available payment method')}</h3>
              </div>

              <>
                {stripePromise && stripeSessionLoading && (
                  <div className="stripe-loading-block">
                    <p>{tt('Préparation du paiement sécurisé...', 'Preparing secure payment...')}</p>
                  </div>
                )}
                {stripePromise && stripeSessionError && !stripeSessionLoading && (
                  <div className="stripe-error-block">
                    <p className="stripe-error">{stripeSessionError}</p>
                    {stripeSessionError.toLowerCase().includes('clé') && stripeSessionError.toLowerCase().includes('stripe') && (
                      <p className="stripe-error-hint">
                        {tt('Allez dans Admin > Paramètres > Configuration Stripe et saisissez vos clés réelles depuis', 'Go to Admin > Settings > Stripe Configuration and enter your real keys from')}{' '}
                        <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer">dashboard.stripe.com</a>.
                      </p>
                    )}
                    <button type="button" className="btn-secondary-checkout" onClick={() => { setStripeSessionError(null); void createStripeSession(); }}>
                      {tt('Réessayer', 'Retry')}
                    </button>
                  </div>
                )}
                {stripePromise && stripeSession && (
                  <Elements stripe={stripePromise} options={{ clientSecret: stripeSession.client_secret }}>
                    <WizardStripePaymentForm session={stripeSession} tt={tt} />
                  </Elements>
                )}
                {!stripePromise && (
                  <div className="card-details-section">
                      <div className="form-group">
                        <label htmlFor="card_number">{tt('Numéro de carte', 'Card number')}</label>
                        <input
                          id="card_number"
                          type="text"
                          value={formData.card_number}
                          onChange={(e) => handleCardFieldChange('card_number', e.target.value)}
                          placeholder="1234 5678 9012 3456"
                        />
                        {errors.card_number && <span className="error-message">{errors.card_number}</span>}
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="card_expiry">{tt('Expiration', 'Expiry')}</label>
                          <input
                            id="card_expiry"
                            type="text"
                            value={formData.card_expiry}
                            onChange={(e) => handleCardFieldChange('card_expiry', e.target.value)}
                            placeholder="MM/YY"
                          />
                          {errors.card_expiry && <span className="error-message">{errors.card_expiry}</span>}
                        </div>
                        <div className="form-group">
                          <label htmlFor="card_cvv">CVV</label>
                          <input
                            id="card_cvv"
                            type="text"
                            value={formData.card_cvv}
                            onChange={(e) => handleCardFieldChange('card_cvv', e.target.value)}
                            placeholder="123"
                          />
                          {errors.card_cvv && <span className="error-message">{errors.card_cvv}</span>}
                        </div>
                      </div>
                      <div className="form-group">
                        <label htmlFor="cardholder_name">{tt('Titulaire de la carte', 'Cardholder name')}</label>
                        <input
                          id="cardholder_name"
                          type="text"
                          value={formData.cardholder_name}
                          onChange={(e) => handleCardFieldChange('cardholder_name', e.target.value)}
                          placeholder={tt('Nom Prénom', 'First Last')}
                        />
                        {errors.cardholder_name && <span className="error-message">{errors.cardholder_name}</span>}
                      </div>
                  </div>
                )}
              </>

              <div className="checkout-summary wizard-summary-inline">
                <h3 className="summary-title">{tt('Récapitulatif final', 'Final summary')}</h3>
                <div className="total-row">
                  <span>{tt('Sous-total', 'Subtotal')}</span>
                  <span>{formatPrice(subtotal)}</span>
                </div>
                <div className="total-row">
                  <span>{tt('Livraison standard', 'Standard shipping')}</span>
                  <span>{formatPrice(shippingCost)}</span>
                </div>
                <div className="total-row total-final">
                  <span>{tt('Total', 'Total')}</span>
                  <span>{formatPrice(total)}</span>
                </div>
              </div>

              <div className="wizard-actions">
                {!(stripePromise && stripeSession) && (
                  <button type="button" className="btn-place-order" onClick={handlePlaceOrder} disabled={submitting}>
                    {submitting ? tt('Validation...', 'Processing...') : tt('Valider et confirmer', 'Validate and confirm')}
                  </button>
                )}
                <button type="button" className="btn-secondary-checkout" onClick={goToPreviousStep}>
                  {tt("Retour à l'étape précédente", 'Back to previous step')}
                </button>
              </div>
            </section>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};
